"use client";

/**
 * Component containing search input and suggestions
 *
 * This component is responsible for rendering autocomplete suggestions, submitting search queries,
 * and clearing search results.
 *
 * The input value (search query) is managed using both internal state and props. It can be set in
 * three ways:
 *   • user editing input value (triggers suggestion fetch)
 *   • user following link which changes URL param q (triggers search results fetch)
 *   • user selecting item from autocomplete menu (triggers search results fetch)
 */

import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

import { useCombobox } from "downshift";
import AutocompleteMenu from "../AutocompleteMenu";
import Button from "../Button";
import Flex from "../Flex";
import { Element } from "../Input/Input";

import Icon from "../Icon";
import { getColor } from "../utils";
import Text from "../Text";

export type Suggestion = {
  display: string;
  token: string;
};

type Props = {
  query?: string;
  suggestions?: Suggestion[] | Suggestion[][];
  fetchSuggestions: (query: string) => void;
  submitSearch: (query: string) => void;
  clearSearch: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  background?: string;
  disabled?: boolean;
};

type DisplayItem = 0 | (Suggestion & { idx: number });

const StyledInput = styled(Element)<{ background?: string }>`
  background: ${props => getColor(props.background, "grayLightest")};
`;

const SearchField: React.FC<Props> = ({
  query: queryProp = "",
  suggestions = [],
  autoFocus = false,
  placeholder = "Search",
  clearSearch,
  submitSearch,
  fetchSuggestions,
  background,
  disabled,
  ...rest
}) => {
  const [query, setQuery] = useState(queryProp || ""),
    onQueryChanged = (q: string) => {
      setQuery(q);
      if (fetchSuggestions && q) {
        fetchSuggestions(q);
      }
      if (!q) clearSearch();
    },
    clearSearchQuery = () => {
      setQuery("");
      clearSearch();
    },
    submitSearchQuery = (q?: string) => {
      if (q && q !== query) {
        setQuery(q);
      }

      const qry = q || query;

      if (qry) {
        submitSearch(qry);
      } else {
        // User hit enter with empty query; go back to full kit
        clearSearch();
      }
    };

  /**
   *  If the query prop changes from an outside source (ex. Tags),
   *  update the internal query state variable to match the change
   * */
  useEffect(() => {
    if (queryProp !== query) setQuery(queryProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryProp]);

  // Normalize the suggestion list into a flat array of objects
  // The normalized list can contain Suggestions or `0` to indicate a divider
  const displayItems = useMemo(() => {
    function validateSuggestionFormat(suggs: Suggestion[] | Suggestion[][]): Suggestion[][] {
      if (!suggs) return null;
      if (Array.isArray(suggs[0])) return suggs as Suggestion[][];
      if (!suggs.length) return [];
      return [suggs as Suggestion[]];
    }
    let _count = 0;
    const _items = validateSuggestionFormat(suggestions)
      .filter(s => s.length)
      .reduce((res, items) => {
        if (res.length) res.push(0);
        const indexedItems = items.map((i, idx) => ({ ...i, idx: _count + idx }));
        _count += items.length;
        return [...res, ...indexedItems];
      }, [] as Array<DisplayItem>) as Array<DisplayItem>;
    return _items;
  }, [suggestions]);

  const items = displayItems.filter(i => i !== 0);

  const {
    isOpen,
    closeMenu,
    getMenuProps,
    getInputProps,
    selectedItem,
    highlightedIndex,
    getItemProps,
  } = useCombobox<DisplayItem>({
    items,
    inputValue: query,
    onInputValueChange: ({ inputValue }) => onQueryChanged(inputValue),
    onHighlightedIndexChange: event => {
      const isArrowEvent =
        event.type === useCombobox.stateChangeTypes.InputKeyDownArrowUp ||
        event.type === useCombobox.stateChangeTypes.InputKeyDownArrowDown;

      if (isArrowEvent) {
        const item = items[event.highlightedIndex];
        if (item) {
          setQuery(item.display);
        }
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        submitSearchQuery(selectedItem.token);
      }
    },
    itemToString: i => (i ? i.token : ""),
  });

  function renderResetButton() {
    return query ? (
      <Button
        id="search-field-clear-button"
        buttonStyle="tertiary"
        size="regular"
        position="absolute"
        top="calc(50% - 12px)"
        right="6px"
        onClick={clearSearchQuery}
        icon="close"
      />
    ) : (
      <Icon
        data-testid="search-field-icon"
        iconId="search"
        fill="grayDark"
        position="absolute"
        right="6px"
        top="calc(50% - 12px)"
      />
    );
  }

  const showMenu = isOpen && query && items.length;

  return (
    <Flex width="100%" {...rest}>
      <StyledInput
        styleOverrides={{ pr: "28px" }}
        background={background}
        data-testid="search-field-input"
        disabled={disabled}
        {...getInputProps({
          id: "search-input",
          autoFocus: autoFocus,
          type: "search",
          placeholder,
          onKeyDown: e => {
            if (e.key === "Enter" && highlightedIndex < 0) {
              closeMenu();
              submitSearchQuery();
            }
          },
        })}
      />
      {renderResetButton()}
      <AutocompleteMenu
        data-testid="search-field-autocomplete"
        display={showMenu ? "visible" : "none"}
        {...getMenuProps()}>
        {displayItems.map((item, idx) =>
          item === 0 ? (
            <AutocompleteMenu.Divider key={`divider-${idx}`} data-testid="search-field-divider" />
          ) : (
            <AutocompleteMenu.Item
              data-testid="search-field-suggestion"
              key={`item-${item.idx}`}
              isActive={highlightedIndex === item.idx}
              {...getItemProps({
                item,
                index: item.idx,
                isSelected: selectedItem === item.idx,
              })}>
              <Text truncate>{item.display}</Text>
            </AutocompleteMenu.Item>
          )
        )}
      </AutocompleteMenu>
    </Flex>
  );
};

export default SearchField;
